import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';

export interface ThemeOptionChoice {
  value: string;
  label: string;
}

export interface ThemeOption {
  key: string;
  label: string;
  type: 'color' | 'select' | 'text' | 'toggle';
  default: string;
  group?: string;
  help?: string;
  choices?: ThemeOptionChoice[];
  cssVar?: string;
}

export interface DemoPage {
  slug: string;
  title: string;
  locale?: string;
  blocks: Array<{ type: string; order?: number; data: Record<string, unknown> }>;
}

export interface ThemeDemo {
  slug: string;
  name: string;
  description?: string;
  screenshot?: string;
  pages: DemoPage[];
}

export interface PackManifest {
  slug: string;
  name: string;
  options: ThemeOption[];
  demos: ThemeDemo[];
}

export async function fetchManifests(frontendUrl: string): Promise<PackManifest[]> {
  const res = await fetch(`${frontendUrl}/api/theme-packs`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('No se pudieron cargar los packs del front');
  const json = (await res.json()) as { data?: PackManifest[] };
  return json.data ?? [];
}

interface CustomizerProps {
  packSlug: string | null;
  themeSlug: string;
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  frontendUrl: string;
}

export function PackCustomizer({ packSlug, themeSlug, values, onChange, frontendUrl }: CustomizerProps) {
  const { data: manifests, isLoading, isError } = useQuery({
    queryKey: ['theme-packs'],
    queryFn: () => fetchManifests(frontendUrl),
  });

  const manifest = useMemo(
    () => manifests?.find((m) => m.slug === (packSlug ?? themeSlug)) ?? null,
    [manifests, packSlug, themeSlug],
  );

  const groups = useMemo(() => {
    const map = new Map<string, ThemeOption[]>();
    for (const opt of manifest?.options ?? []) {
      const g = opt.group ?? 'General';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(opt);
    }
    return [...map.entries()];
  }, [manifest]);

  if (isLoading) return <p className="text-sm text-neutral-500">Cargando opciones del pack…</p>;
  if (isError) {
    return (
      <p className="text-sm text-amber-600">
        No se pudo conectar con el front ({frontendUrl}). Comprueba que está en marcha.
      </p>
    );
  }
  if (!manifest) {
    return (
      <p className="text-sm text-neutral-500">
        Este tema apunta al pack «{packSlug ?? themeSlug}», que no está instalado en el front actual.
      </p>
    );
  }
  if (manifest.options.length === 0) {
    return <p className="text-sm text-neutral-500">El pack «{manifest.name}» no expone opciones de personalización.</p>;
  }

  function setValue(key: string, value: string) {
    onChange({ ...values, [key]: value });
  }

  function resetDefaults() {
    const next: Record<string, string> = {};
    for (const opt of manifest?.options ?? []) next[opt.key] = opt.default;
    onChange(next);
  }

  return (
    <div className="max-w-xl space-y-5">
      <p className="text-xs text-neutral-500">
        Opciones del pack <strong>{manifest.name}</strong>. Los cambios se ven al instante en la vista previa de abajo.
      </p>
      {groups.map(([group, opts]) => (
        <div key={group} className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">{group}</h4>
          <div className="grid gap-3 sm:grid-cols-2">
            {opts.map((opt) => (
              <OptionField key={opt.key} opt={opt} value={values[opt.key] ?? opt.default} onChange={(v) => setValue(opt.key, v)} />
            ))}
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={resetDefaults}>
        <RotateCcw className="size-3.5" /> Restablecer valores del pack
      </Button>
    </div>
  );
}

function OptionField({ opt, value, onChange }: { opt: ThemeOption; value: string; onChange: (v: string) => void }) {
  if (opt.type === 'color') {
    return (
      <div>
        <Label className="text-xs">{opt.label}</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-12 cursor-pointer rounded border border-neutral-200"
          />
          <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 font-mono text-xs" />
        </div>
        {opt.help && <p className="mt-1 text-[11px] text-neutral-400">{opt.help}</p>}
      </div>
    );
  }

  if (opt.type === 'select') {
    return (
      <div>
        <Label className="text-xs">{opt.label}</Label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
        >
          {(opt.choices ?? []).map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        {opt.help && <p className="mt-1 text-[11px] text-neutral-400">{opt.help}</p>}
      </div>
    );
  }

  if (opt.type === 'toggle') {
    const on = value === 'true' || value === '1';
    return (
      <label className="flex cursor-pointer items-center justify-between gap-2 py-1">
        <span className="text-xs">{opt.label}</span>
        <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} className="size-4" />
      </label>
    );
  }

  return (
    <div>
      <Label className="text-xs">{opt.label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm" />
      {opt.help && <p className="mt-1 text-[11px] text-neutral-400">{opt.help}</p>}
    </div>
  );
}
